/* eslint-disable jsx-a11y/no-autofocus */
// AccountHeader.tsx
import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { Edit2, Check, X, Plus } from 'lucide-react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';

import { UserRole } from 'utils/bankStatements';
import { useAccountNumberPermission } from './UsePermissions';
import ReconcileButton from './ReconcileButton';

const AccountHeader = ({
  account,
  editingField,
  onEdit,
  onSave,
  onCancel,
  onAddTransaction,
  statementId,
}) => {
  const { canEdit: canEditAccountNumber } = useAccountNumberPermission();
  const userRole = useSelector((state) => get(state, 'bankStatements.settings.userRole'));
  const inputRef = useRef(null);

  const getBalanceValue = (field) => {
    const value = account[field];
    if (typeof value === 'string') {
      return parseFloat(value).toFixed(2);
    }
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    return '0.00';
  };

  const editButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    border: '1px solid #E5E7EB',
    borderRadius: '4px',
    marginLeft: '4px',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  const valueStyle = {
    fontWeight: 700,
  };

  return (
    <tr className="bg-blue-50/50 border-y border-gray-200">
      {/* First td with account number and type */}
      <td colSpan={3} className="p-4">
        <div className="space-y-2">
          <div className="flex items-center">
            <span className="text-sm text-gray-500 min-w-[80px]">
              Account:
              {' '}
            </span>
            {editingField === 'accountNumber' ? (
              <div className="flex items-center">
                <input
                  ref={inputRef}
                  type="text"
                  defaultValue={account.accountNumber}
                  className="w-40 px-2 py-1 text-sm border rounded"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onSave('accountNumber', e.currentTarget.value);
                    }
                    if (e.key === 'Escape') onCancel();
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => onSave('accountNumber', (inputRef.current && inputRef.current.value) || '')}
                  style={editButtonStyle}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                    e.currentTarget.style.borderColor = '#D1D5DB';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <Check
                    aria-label="Save"
                    style={{ width: '14px', height: '14px', color: '#059669' }}
                  />
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  style={editButtonStyle}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                    e.currentTarget.style.borderColor = '#D1D5DB';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <X
                    aria-label="Cancel"
                    style={{ width: '14px', height: '14px', color: '#4B5563' }}
                  />
                </button>
              </div>
            ) : (
              <Fragment>
                <span className="text-sm" style={valueStyle}>
                  {account.accountNumber}
                </span>
                {canEditAccountNumber
                && (
                  <button
                    type="button"
                    onClick={() => onEdit('accountNumber')}
                    style={editButtonStyle}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                      e.currentTarget.style.borderColor = '#D1D5DB';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#FFFFFF';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }}
                  >
                    <Edit2
                      aria-label="Edit"
                      style={{
                        width: '14px',
                        height: '14px',
                        color: '#4B5563',
                      }}
                    />
                  </button>
                )}
              </Fragment>
            )}
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-500 min-w-[80px]">Type: </span>
            {editingField === 'accountType' ? (
              <div className="flex items-center">
                <select
                  ref={inputRef}
                  defaultValue={account.accountType}
                  className="w-40 px-2 py-1 text-sm border rounded"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onSave('accountType', e.currentTarget.value);
                    }
                    if (e.key === 'Escape') onCancel();
                  }}
                  autoFocus
                >
                  <option value="Checking">Checking</option>
                  <option value="Savings">Savings</option>
                </select>
                <button
                  type="button"
                  onClick={() => onSave('accountType', (inputRef.current && inputRef.current.value) || '')}
                  style={editButtonStyle}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                    e.currentTarget.style.borderColor = '#D1D5DB';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <Check
                    aria-label="Save"
                    style={{ width: '14px', height: '14px', color: '#059669' }}
                  />
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  style={editButtonStyle}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                    e.currentTarget.style.borderColor = '#D1D5DB';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <X
                    aria-label="Cancel"
                    style={{ width: '14px', height: '14px', color: '#4B5563' }}
                  />
                </button>
              </div>
            ) : (
              <>
                <span className="text-sm" style={valueStyle}>
                  {account.accountType}
                </span>
                {canEditAccountNumber && (
                  <button
                    type="button"
                    onClick={() => onEdit('accountType')}
                    style={editButtonStyle}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                      e.currentTarget.style.borderColor = '#D1D5DB';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#FFFFFF';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }}
                  >
                    <Edit2
                      aria-label="Edit"
                      style={{
                        width: '14px',
                        height: '14px',
                        color: '#4B5563',
                      }}
                    />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </td>
      {/* Second td with balance information */}
      <td colSpan={1} className="p-4">
        <div className="flex flex-col">
          <div
            className="mb-2"
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              alignItems: 'center',
            }}
          >
            <span className="text-sm text-gray-500 self-center">
              Starting Balance:
              {' '}
            </span>
            <div className="flex items-center">
              {editingField === 'beginningBalance' ? (
                <Fragment>
                  <input
                    ref={inputRef}
                    type="number"
                    step="0.01"
                    defaultValue={getBalanceValue('beginningBalance')}
                    className="w-32 px-2 py-1 text-sm border rounded h-[28px] no-spinners mb-0"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onSave('beginningBalance', e.currentTarget.value);
                      }
                      if (e.key === 'Escape') onCancel();
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => onSave('beginningBalance', (inputRef.current && inputRef.current.value) || '')}
                    style={editButtonStyle}
                  >
                    <Check
                      aria-label="Save"
                      style={{
                        width: '14px',
                        height: '14px',
                        color: '#059669',
                      }}
                    />
                  </button>
                  <button type="button" onClick={onCancel} style={editButtonStyle}>
                    <X
                      aria-label="Cancel"
                      style={{
                        width: '14px',
                        height: '14px',
                        color: '#4B5563',
                      }}
                    />
                  </button>
                </Fragment>
              ) : (
                <Fragment>
                  <span
                    className="text-sm inline-flex items-center h-[28px]"
                    style={valueStyle}
                  >
                    $
                    {getBalanceValue('beginningBalance')}
                  </span>
                  <button
                    type="button"
                    onClick={() => onEdit('beginningBalance')}
                    style={editButtonStyle}
                  >
                    <Edit2
                      aria-label="Edit"
                      style={{
                        width: '14px',
                        height: '14px',
                        color: '#4B5563',
                      }}
                    />
                  </button>
                </Fragment>
              )}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              alignItems: 'center',
            }}
          >
            <span className="text-sm text-gray-500 self-center">
              Ending Balance:
              {' '}
            </span>
            <div className="flex items-center">
              {editingField === 'endingBalance' ? (
                <Fragment>
                  <input
                    ref={inputRef}
                    type="number"
                    step="0.01"
                    defaultValue={getBalanceValue('endingBalance')}
                    className="w-32 px-2 py-1 text-sm border rounded h-[28px] no-spinners mb-0"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onSave('endingBalance', e.currentTarget.value);
                      }
                      if (e.key === 'Escape') onCancel();
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => onSave('endingBalance', (inputRef.current && inputRef.current.value) || '')}
                    style={editButtonStyle}
                  >
                    <Check
                      aria-label="Save"
                      style={{
                        width: '14px',
                        height: '14px',
                        color: '#059669',
                      }}
                    />
                  </button>
                  <button type="button" onClick={onCancel} style={editButtonStyle}>
                    <X
                      style={{
                        width: '14px',
                        height: '14px',
                        color: '#4B5563',
                      }}
                      aria-label="Cancel"
                    />
                  </button>
                </Fragment>
              ) : (
                <Fragment>
                  <span
                    className="text-sm inline-flex items-center h-[28px]"
                    style={valueStyle}
                  >
                    $
                    {getBalanceValue('endingBalance')}
                  </span>
                  <button
                    type="button"
                    onClick={() => onEdit('endingBalance')}
                    style={editButtonStyle}
                  >
                    <Edit2
                      aria-label="Edit"
                      style={{
                        width: '14px',
                        height: '14px',
                        color: '#4B5563',
                      }}
                    />
                  </button>
                </Fragment>
              )}
            </div>
          </div>
        </div>
      </td>
      {/* Third td with action buttons */}
      <td colSpan={2} className="p-4">
        <div
          className="flex items-center justify-center gap-4" // Updated flex classes
          onClick={e => e.stopPropagation()}
          style={{ minWidth: '300px' }} // Added to ensure enough space
        >
          {userRole && statementId && (
            <div style={{ width: '140px' }}>
              {' '}
              {/* Wrapper to match width */}
              <ReconcileButton
                statementId={statementId}
                accountId={account.id}
                userRole={userRole}
                isReconciled={account.isReconciled}
                accountNumber={account.accountNumber}
              />
            </div>
          )}
          {(userRole === UserRole.Admin
          || userRole === UserRole.Underwriter)
          && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Add Transaction button clicked', {
                  accountId: account.id,
                });
                onAddTransaction(account.id);
              }}
              className="confidence-badge manual text-xs flex items-center"
              style={{
                width: '140px',
                justifyContent: 'center',
                height: '32px', // Explicit height
                padding: '0 12px', // Consistent padding
              }}
            >
              {/* Matched icon size */}
              <Plus className="w-4 h-4 mr-1" />
              Add Transaction
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

AccountHeader.propTypes = {
  account: PropTypes.object.isRequired,
  editingField: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onAddTransaction: PropTypes.func.isRequired,
  statementId: PropTypes.string.isRequired,
};

export default AccountHeader;
